import React, { useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import MapView from "@arcgis/core/views/MapView";
import WebMap from "@arcgis/core/WebMap";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Graphic from "@arcgis/core/Graphic";
import * as geometryJsonUtils from "@arcgis/core/geometry/support/jsonUtils";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import Color from "@arcgis/core/Color";
import esriConfig from "@arcgis/core/config";
import IdentityManager from "@arcgis/core/identity/IdentityManager";

import LayerList from "@arcgis/core/widgets/LayerList";
import Legend from "@arcgis/core/widgets/Legend";
import BasemapGallery from "@arcgis/core/widgets/BasemapGallery";
import Basemap from "@arcgis/core/Basemap";
import TileLayer from "@arcgis/core/layers/TileLayer";
import MapImageLayer from "@arcgis/core/layers/MapImageLayer";
import WMSLayer from "@arcgis/core/layers/WMSLayer";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import Measurement from "@arcgis/core/widgets/Measurement";
import Expand from "@arcgis/core/widgets/Expand";
import Home from "@arcgis/core/widgets/Home";
import ScaleBar from "@arcgis/core/widgets/ScaleBar";
import Zoom from "@arcgis/core/widgets/Zoom";

import * as webMercatorUtils from "@arcgis/core/geometry/support/webMercatorUtils";
import Point from "@arcgis/core/geometry/Point";

import { ArcGISMapProps, ArcGISMapHandle } from "../../types/config";

import { drawWidget } from "./custom-widgets/Draw";
import { googleStreetView } from "./custom-widgets/GoogleStreetView";
import { installCompactCoordinate } from "./custom-widgets/Coordinate";
import streetViewIcon from "./custom-widgets/images/googleStreetIcon.png"

import tnImagery2018 from "./images/tnimagerybm2018.png";
import esristreetsbm from "./images/esristreetsbm.png";
import esriimagerybm from "./images/esriimagerybm.png";
import esritopobm from "./images/esritopobm.png";
import esrilightgray from "./images/esrilightgray.png";
import googleHybrid from "./images/googlehybridbm.png";

import config from "../../config/config.json";
import "./styles/Map.css"
import * as CSS from "csstype";

const MapDivStyle: CSS.Properties = { height: "100%", width: "100%", position: "relative" };

const ArcGISMap = forwardRef<ArcGISMapHandle, ArcGISMapProps>(({ itemId, onMapIdentify, onCondoUnitsFound, onLoadingStart, onLoadingEnd }, ref) => {
    const mapDiv = useRef<HTMLDivElement>(null);
    const viewRef = useRef<MapView | null>(null);
    const highlightLayerRef = useRef<GraphicsLayer | null>(null);
    const geomLayerRef = useRef<__esri.FeatureLayer | null>(null);
    const printFeatureCacheRef = useRef<Record<string, { features: any[]; geometryType: string; labelingInfo: any[]; isParcelLayer: boolean; rendererJson: any }>>({});

    useEffect(() => {
        if (!mapDiv.current) return;

        esriConfig.portalUrl = config.map.portalUrl;

        IdentityManager.registerToken({
            server: config.map.portalUrl,
            token: "",
            expires: Date.now() + 365 * 24 * 60 * 60 * 1000
        } as any);

        const webmap = new WebMap({ portalItem: { id: itemId } });
        const view = new MapView({
            container: mapDiv.current,
            map: webmap,
            popupEnabled: false
        });
        viewRef.current = view;

        const topRightRow = document.createElement("div");
        topRightRow.className = "custom-top-right-row";
        Object.assign(topRightRow.style, { display: "flex", gap: "8px", alignItems: "center", padding: "8px" });
        view.ui.add(topRightRow, { position: "top-right", index: 0 });

        const styleEl = document.createElement("style");
        styleEl.textContent = `
          .custom-top-right-row .expand-wrap { position: relative; }
          .custom-top-right-row .expand-wrap .esri-expand__panel {
            right: 0 !important; left: auto !important; top: calc(100% + 8px);
            max-height: min(60vh, calc(100% - 56px)); max-width: min(600px, calc(100vw - 32px));
            overflow: auto; box-sizing: border-box;
          }
          .custom-top-right-row .esri-widget--button { margin: 0; }
          .esri-ui, .esri-ui-corner, .esri-ui-top-right, .esri-ui-inner-container { overflow: visible !important; }
          .esri-layer-list, .esri-basemap-gallery { position: static !important; margin-top: 0 !important; }
          .esri-icon-layer-list, .esri-icon-basemap, .esri-icon-feature-layer, .esri-icon-radio-unchecked { position: static !important; top:auto !important; left:auto !important; }

          .exb-btn { width:32px; height:32px; border:1px solid #dcdcdc; border-radius:4px; background:#fff; cursor:pointer; display:grid; place-items:center; }
          .exb-btn svg { width:18px; height:18px; fill:#2b2b2b; }

          .exb-pop { z-index:5; border-radius:8px; box-shadow:0 8px 24px rgba(0,0,0,.15); padding:12px; }
          .exb-row { display:grid; grid-template-columns:auto 1fr auto; gap:8px; align-items:center; margin:8px 0; }
          .exb-sw { display:grid; grid-template-columns: repeat(10, 18px); gap:6px; margin:8px 0; }
          .exb-swatch { width:18px; height:18px; border-radius:50%; border:1px solid #ccc; cursor:pointer; }
          .exb-title { font-weight:600; margin-bottom:8px; display:flex; align-items:center; gap:8px; }
          .exb-prev { width:24px; height:24px; border-radius:50%; border:2px solid #2b2b2b; background:#0079c133; }

          .esri-button { padding:6px 10px; border:1px solid #dcdcdc; background:#fff; border-radius:4px; cursor:pointer; font-size:12px; }
          .esri-button:disabled { opacity:.5; cursor:default; }
        `;
        mapDiv.current.appendChild(styleEl);

        const wrap = () => {
            const w = document.createElement("div");
            w.className = "expand-wrap";
            topRightRow.appendChild(w);
            return w;
        };

        view.when(async () => {
            const hl = new GraphicsLayer({ listMode: "hide" });
            webmap.add(hl);
            highlightLayerRef.current = hl;

            const legend = new Legend({ view });
            const layerList = new LayerList({
                view,
                visibilityAppearance: "checkbox",
                listItemCreatedFunction: (event: any) => {
                    const item = event.item;
                    item.panel = { content: "legend", open: false };
                }
            });

            // Map thumbnail filenames to imported images
            const thumbnailMap: Record<string, string> = {
                "images/tnimagerybm2018.png": tnImagery2018,
                "images/esristreetsbm.png": esristreetsbm,
                "images/esriimagerybm.png": esriimagerybm,
                "images/esritopobm.png": esritopobm,
                "images/esrilightgray.png": esrilightgray,
                "images/googlehybridbm.png": googleHybrid
            };

            // Create custom basemaps from config
            const customBasemaps = await Promise.all(config.basemapGallery.basemaps.map(async bmConfig => {
                const baseLayers = await Promise.all(bmConfig.layers.map(async layerConfig => {
                    // Try to load as TileLayer first
                    const tileLayer = new TileLayer({ url: layerConfig.url });
                    try {
                        await tileLayer.load();
                        // Check if it has tileInfo (is a cached service)
                        if (tileLayer.tileInfo) {
                            return tileLayer;
                        }
                    } catch (error) {
                        console.warn(`TileLayer failed for ${layerConfig.url}, trying MapImageLayer`);
                    }

                    // Fall back to MapImageLayer for dynamic services
                    return new MapImageLayer({ url: layerConfig.url });
                }));

                return new Basemap({
                    baseLayers: baseLayers,
                    title: bmConfig.title,
                    thumbnailUrl: thumbnailMap[bmConfig.thumbnailUrl] || bmConfig.thumbnailUrl
                });
            }));


            const wmsBasemaps = config.basemapGallery.wmsBasemaps ? await Promise.all(
                config.basemapGallery.wmsBasemaps.map(async (wmsConfig: any) => {

                    let wmsUrl = wmsConfig.url;

                    if (process.env.NODE_ENV === 'development') {

                        const url = new URL(wmsConfig.url);
                        wmsUrl = `http://localhost:3000/wms-proxy${url.pathname}`;
                    }

                    const corsServers = (esriConfig.request as any).corsEnabledServers || [];

                    if (process.env.NODE_ENV !== 'development') {
                        const baseUrl = new URL(wmsUrl).origin;
                        [wmsUrl, baseUrl, 'https://tnmap-wms.giza.cloud'].forEach(url => {
                            if (!corsServers.includes(url)) {
                                corsServers.push(url);
                            }
                        });
                        (esriConfig.request as any).corsEnabledServers = corsServers;
                    }

                    const visibleLayers = wmsConfig.layers.map((layer: any) => layer.name);
                    const layerInfos = wmsConfig.layers.map((layer: any) => ({
                        name: layer.name,
                        title: layer.title
                    }));

                    const wmsLayer = new WMSLayer({
                        url: wmsUrl,
                        title: wmsConfig.title,
                        imageFormat: "png",
                        version: "1.3.0",
                        visible: true,
                        spatialReference: { wkid: 3857 },
                        customParameters: {
                            FORMAT: "image/png"
                        },
                        fullExtent: {
                            xmin: -20037508.342787,
                            ymin: -20037508.342787,
                            xmax: 20037508.342787,
                            ymax: 20037508.342787,
                            spatialReference: { wkid: 3857 }
                        },
                        resourceInfo: {
                            copyright: wmsConfig.copyright,
                            description: wmsConfig.description,
                            extent: {
                                xmin: -20037508.342787,
                                ymin: -20037508.342787,
                                xmax: 20037508.342787,
                                ymax: 20037508.342787,
                                spatialReference: { wkid: 3857 }
                            },
                            layerInfos: layerInfos,
                            spatialReferences: [3857],
                            version: "1.3.0"
                        },
                        visibleLayers: visibleLayers,
                        sublayers: wmsConfig.layers.map((layer: any) => ({
                            name: layer.name,
                            title: layer.title,
                            visible: true
                        }))
                    } as any);

                    // Always store the original (non-proxy) URL for the print service
                    (wmsLayer as any)._originalWmsUrl = wmsConfig.url;

                    try {
                        await wmsLayer.load();

                        (wmsLayer as any).mapUrl = wmsUrl;

                    } catch (error) {
                        console.error('[WMS] Failed to load WMS layer:', wmsConfig.title, error);
                    }

                    return new Basemap({
                        baseLayers: [wmsLayer],
                        title: wmsConfig.title,
                        thumbnailUrl: thumbnailMap[wmsConfig.thumbnailUrl] || wmsConfig.thumbnailUrl
                    });
                })
            ) : [];

            const allCustomBasemaps = [...customBasemaps, ...wmsBasemaps];

            // Create BasemapGallery with custom basemaps only (overwrite portal basemaps)
            const basemapGallery = new BasemapGallery({ view });

            // Replace portal basemaps with custom basemaps if overwriteDefault is true
            const source = basemapGallery.source as __esri.PortalBasemapsSource;
            const addCustomBasemaps = () => {
                if (source.basemaps && source.basemaps.length > 0) {
                    if (config.basemapGallery.overwriteDefault) {
                        // Clear all portal basemaps and use only custom basemaps
                        source.basemaps.removeAll();
                        allCustomBasemaps.forEach((bm) => {
                            source.basemaps.add(bm);
                        });
                    } else {
                        // Add custom basemaps to the beginning
                        allCustomBasemaps.reverse().forEach((bm) => {
                            source.basemaps.unshift(bm);
                        });
                    }
                } else {
                    // If not loaded yet, try again in 500ms
                    setTimeout(addCustomBasemaps, 500);
                }
            };
            addCustomBasemaps();

            let lastWmsBasemapId: string | null = null;
            basemapGallery.watch("activeBasemap", (newBasemap) => {
                if (!newBasemap) return;

                // Remove any existing WMS layers from the map
                const layersToRemove = webmap.layers.filter((layer) => {
                    return layer.type === "wms" && (layer as any)._isBasemapLayer;
                });
                layersToRemove.forEach((layer) => {
                    webmap.layers.remove(layer);
                });

                // Check if the new basemap contains a WMS layer
                const wmsBaseLayer = newBasemap.baseLayers.find((layer: any) => layer.type === "wms");
                if (wmsBaseLayer) {
                    // Guard against duplicate fires for the same basemap
                    if (lastWmsBasemapId === newBasemap.id) return;
                    lastWmsBasemapId = newBasemap.id;

                    // Create a fresh WMS layer instance with the same properties
                    const freshLayer = new WMSLayer({
                        url: wmsBaseLayer.url,
                        title: wmsBaseLayer.title,
                        imageFormat: wmsBaseLayer.imageFormat,
                        version: wmsBaseLayer.version,
                        visible: true,
                        spatialReference: wmsBaseLayer.spatialReference,
                        customParameters: (wmsBaseLayer as any).customParameters,
                        fullExtent: wmsBaseLayer.fullExtent,
                        resourceInfo: (wmsBaseLayer as any).resourceInfo,
                        visibleLayers: (wmsBaseLayer as any).visibleLayers,
                        sublayers: wmsBaseLayer.sublayers
                    } as any);

                    (freshLayer as any)._isBasemapLayer = true;
                    (freshLayer as any)._originalWmsUrl = (wmsBaseLayer as any)._originalWmsUrl;

                    // Add the fresh WMS layer to the map at index 0 (bottom)
                    webmap.layers.add(freshLayer, 0);
                } else {
                    lastWmsBasemapId = null;
                }
            });

            // @ts-ignore - module resolves at runtime via bundler
            await import("@arcgis/map-components/components/arcgis-print");
            const printContainer = document.createElement("div");
            const printEl = document.createElement("arcgis-print") as HTMLElement & {
                view: MapView;
                printServiceUrl: string;
                templateOptions: any;
                destroy: () => Promise<void>;
            };
            printEl.view = view;
            printEl.printServiceUrl = config.exportWebMapService;
            printEl.templateOptions = { legendEnabled: false };
            printContainer.appendChild(printEl);

            const updatePrintFeatureCache = async () => {
                if (!view.extent) return;
                const cache: Record<string, { features: any[]; geometryType: string; labelingInfo: any[]; isParcelLayer: boolean; rendererJson: any }> = {};
                const featureLayers = webmap.allLayers.filter(
                    (l: any) => l.type === "feature" && l.visible
                ) as __esri.Collection<FeatureLayer>;

                const geometryLayerUrl = config.geometryLayer || config.parcelLayer.replace(/\/\d+$/, '/46');

                await Promise.all(featureLayers.map(async (layer: FeatureLayer) => {
                    try {
                        const result = await layer.queryFeatures({
                            geometry: view.extent,
                            spatialRelationship: "intersects",
                            returnGeometry: true,
                            outFields: ["*"],
                            num: 2000,
                            maxAllowableOffset: 2
                        } as any);
                        if (result.features.length > 0) {
                            const liveLabelingInfo = layer.labelingInfo ?? [];
                            const hasLiveLabelingInfo = liveLabelingInfo.length > 0;

                            // Detect the parcel/cadastral layer to keep its hardcoded PARCELWP logic.
                            // All other labeled layers use their actual label expressions.
                            const isParcelLyr =
                                layer.url === geometryLayerUrl ||
                                layer.url === config.parcelLayer ||
                                layer.title?.toLowerCase().includes("parcel") ||
                                layer.title?.toLowerCase().includes("cadastral");

                            const labelingInfo = hasLiveLabelingInfo
                                ? (isParcelLyr
                                    ? [{
                                        labelPlacement: "esriServerPolygonPlacementAlwaysHorizontal",
                                        labelExpression: "[PARCELWP]",
                                        useCodedValues: false,
                                        symbol: {
                                            type: "esriTS",
                                            color: [0, 0, 0, 255],
                                            backgroundColor: null,
                                            borderLineColor: null,
                                            haloColor: [255, 255, 255, 255],
                                            haloSize: 1,
                                            verticalAlignment: "middle",
                                            horizontalAlignment: "center",
                                            rightToLeft: false,
                                            angle: 0,
                                            xoffset: 0,
                                            yoffset: 0,
                                            font: {
                                                family: "Arial",
                                                size: 8,
                                                style: "normal",
                                                weight: "normal",
                                                decoration: "none"
                                            }
                                        },
                                        minScale: 0,
                                        maxScale: 0,
                                        where: ""
                                    }]
                                    : liveLabelingInfo.map((lc: any) =>
                                        typeof lc.toJSON === "function" ? lc.toJSON() : { ...lc }
                                    ))
                                : [];

                            // Capture the layer's full renderer JSON for use in the print interceptor
                            const rendererJson = (layer.renderer as any)?.toJSON?.() ?? null;

                            cache[layer.id] = {
                                features: result.features.map(f => {
                                    const json = f.toJSON();
                                    delete json.aggregateGeometries;
                                    const geom = json.geometry;
                                    if (geom?.rings) {
                                        geom.rings = geom.rings.map((ring: number[][]) =>
                                            ring.map(([x, y]) => [Math.round(x * 10) / 10, Math.round(y * 10) / 10])
                                        );
                                    } else if (geom?.paths) {
                                        geom.paths = geom.paths.map((path: number[][]) =>
                                            path.map(([x, y]) => [Math.round(x * 10) / 10, Math.round(y * 10) / 10])
                                        );
                                    }
                                    return { geometry: geom, attributes: json.attributes ?? {} };
                                }),
                                geometryType: result.geometryType as string,
                                labelingInfo,
                                isParcelLayer: !!isParcelLyr,
                                rendererJson
                            };
                        }
                    } catch (_) {
                    }
                }));
                printFeatureCacheRef.current = cache;
            };

            let pendingCacheRefresh: Promise<void> | null = null;

            const scheduleOrAwaitCacheRefresh = () => {
                if (!pendingCacheRefresh) {
                    pendingCacheRefresh = updatePrintFeatureCache().finally(() => {
                        pendingCacheRefresh = null;
                    });
                }
                return pendingCacheRefresh;
            };

            // Populate cache after map is ready and whenever the map stops moving
            webmap.when(() => scheduleOrAwaitCacheRefresh());
            view.watch("stationary", (isStationary: boolean) => {
                if (isStationary) scheduleOrAwaitCacheRefresh();
            });

            if (!esriConfig.request.interceptors) esriConfig.request.interceptors = [];
            esriConfig.request.interceptors.push({
                urls: config.exportWebMapService,
                before: async (params: any) => {
                    await scheduleOrAwaitCacheRefresh();

                    const query = params.requestOptions?.query;
                    const webMapStr = query?.Web_Map_as_JSON;
                    if (!webMapStr) return;

                    try {
                        const webMapJson = JSON.parse(webMapStr);
                        if (!webMapJson.operationalLayers) return;

                        console.log("[Print] all operationalLayers before filter:", webMapJson.operationalLayers.map((ol: any) => ({ id: ol.id, title: ol.title, layerType: ol.layerType, type: ol.type, visibility: ol.visibility })));
                        webMapJson.operationalLayers = webMapJson.operationalLayers.filter(
                            (ol: any) => ol.layerType !== "GraphicsLayer" && !(ol.layerType == null && ol.type == null && !ol.title)
                        );
                        console.log("[Print] operationalLayers after filter:", webMapJson.operationalLayers.map((ol: any) => ({ id: ol.id, title: ol.title, layerType: ol.layerType, type: ol.type })));

                        const buildSimpleRenderer = (geomType: string, strokeColor: number[], strokeWidth: number) => {
                            if (geomType === "polyline") {
                                return { type: "simple", symbol: { type: "esriSLS", style: "esriSLSSolid", color: [strokeColor[0], strokeColor[1], strokeColor[2], strokeColor[3] ?? 255], width: strokeWidth } };
                            }
                            if (geomType === "point" || geomType === "multipoint") {
                                return { type: "simple", symbol: { type: "esriSMS", style: "esriSMSCircle", color: [strokeColor[0], strokeColor[1], strokeColor[2], 180], size: 6, outline: { type: "esriSLS", style: "esriSLSSolid", color: [strokeColor[0], strokeColor[1], strokeColor[2], strokeColor[3] ?? 255], width: 1 } } };
                            }
                            return { type: "simple", symbol: { type: "esriSFS", style: "esriSFSSolid", color: [0, 0, 0, 0], outline: { type: "esriSLS", style: "esriSLSSolid", color: [strokeColor[0], strokeColor[1], strokeColor[2], strokeColor[3] ?? 255], width: strokeWidth } } };
                        };

                        const geomTypeMap: Record<string, string> = {
                            polygon: "esriGeometryPolygon",
                            polyline: "esriGeometryPolyline",
                            point: "esriGeometryPoint",
                            multipoint: "esriGeometryMultipoint"
                        };

                        const processLayer = (ol: any): any => {
                            ol.showLabels = false;
                            if (ol.layerType === "GroupLayer" && Array.isArray(ol.layers)) {
                                ol.layers = ol.layers.map(processLayer);
                                return ol;
                            }
                            if (ol.layerType !== "ArcGISFeatureLayer") return ol;

                            const cached = printFeatureCacheRef.current[ol.id];
                            const renderer = cached?.rendererJson ?? ol.layerDefinition?.drawingInfo?.renderer;
                            const rawGeomType: string =
                                cached?.geometryType ??
                                ol.layerDefinition?.geometryType?.replace("esriGeometry", "")?.toLowerCase() ??
                                "polygon";

                            const isCIMSymbol = (sym: any) => sym?.type === "CIMSymbolReference" || !!sym?.symbol;

                            // Build a fallback esri symbol for a given geometry type using a given color array
                            const colorToEsriSymbol = (geomType: string, color: number[]) => {
                                const [r, g, b, a] = color;
                                if (geomType === "polyline") return { type: "esriSLS", style: "esriSLSSolid", color: [r, g, b, a ?? 255], width: 1 };
                                if (geomType === "point" || geomType === "multipoint") return { type: "esriSMS", style: "esriSMSCircle", color: [r, g, b, a ?? 180], size: 6, outline: { type: "esriSLS", style: "esriSLSSolid", color: [r, g, b, a ?? 255], width: 1 } };
                                return { type: "esriSFS", style: "esriSFSSolid", color: [r, g, b, a ?? 255], outline: { type: "esriSLS", style: "esriSLSSolid", color: [r, g, b, a ?? 255], width: 1 } };
                            };

                            const sanitizeRenderer = (r: any): any | null => {
                                if (!r) return null;
                                if (r.type === "simple") {
                                    if (isCIMSymbol(r.symbol)) return null;
                                    return r;
                                }
                                if (r.type === "uniqueValue" || r.type === "classBreaks") {
                                    const infoKey = r.type === "uniqueValue" ? "uniqueValueInfos" : "classBreakInfos";
                                    const infos: any[] = r[infoKey] ?? [];
                                    const sanitizedInfos = infos.map((info: any) => {
                                        if (isCIMSymbol(info.symbol)) {
                                            const cimFill = info.symbol?.symbol?.symbolLayers?.find((sl: any) => sl.type === "CIMSolidFill");
                                            const cimStroke = info.symbol?.symbol?.symbolLayers?.find((sl: any) => sl.type === "CIMSolidStroke");
                                            const fillColor = cimFill?.color ?? [104, 104, 104, 255];
                                            const strokeColor = cimStroke?.color ?? fillColor;
                                            const [fr, fg, fb] = fillColor;
                                            const [sr, sg, sb] = strokeColor;
                                            const symbol = rawGeomType === "polygon"
                                                ? { type: "esriSFS", style: "esriSFSSolid", color: [fr, fg, fb, 255], outline: { type: "esriSLS", style: "esriSLSSolid", color: [sr, sg, sb, 255], width: 1 } }
                                                : colorToEsriSymbol(rawGeomType, fillColor);
                                            return { ...info, symbol };
                                        }
                                        return info;
                                    });
                                    const { authoringInfo: _a, uniqueValueGroups: _g, field2: _f2, field3: _f3, fieldDelimiter: _fd, ...rest } = r;
                                    let finalInfos = sanitizedInfos;
                                    if (r.type === "uniqueValue" && (r.field2 || r.field3)) {
                                        // Re-key each value: original value is "field1<delim>field2[<delim>field3]" — keep only field1 part
                                        const delim: string = r.fieldDelimiter ?? ",";
                                        finalInfos = sanitizedInfos.map((info: any) => ({
                                            ...info,
                                            value: typeof info.value === "string" ? info.value.split(delim)[0].trim() : info.value
                                        }));
                                    }
                                    return { ...rest, [infoKey]: finalInfos };
                                }
                                return r;
                            };

                            let printRenderer: any;
                            const sanitized = sanitizeRenderer(renderer);
                            if (sanitized) {
                                printRenderer = sanitized;
                            } else {
                                // Full CIM renderer — extract stroke color and downgrade to simple esri renderer
                                const strokeLayer = renderer?.symbol?.symbol?.symbolLayers?.find((sl: any) => sl.type === "CIMSolidStroke");
                                const c = strokeLayer?.color ?? [104, 104, 104, 255];
                                const width = strokeLayer?.width ?? 1;
                                printRenderer = buildSimpleRenderer(rawGeomType, c, width);
                            }

                            const isParcel = cached?.isParcelLayer ?? false;
                            const isPolygonGeom = rawGeomType === "polygon";
                            if (!isParcel && isPolygonGeom) {
                                // Sanitize renderer in-place (strip CIM data) and let GP fetch features
                                ol.layerDefinition = ol.layerDefinition ?? {};
                                ol.layerDefinition.drawingInfo = { renderer: printRenderer };
                                // Remove scale restrictions
                                ol.minScale = 0;
                                ol.maxScale = 0;
                                ol.visibility = true;
                                ol.showLabels = false;
                                return ol;
                            }

                            if (!cached || cached.features.length === 0) {
                                // Parcel layer but no cached features — pass through as service layer
                                ol.layerDefinition = ol.layerDefinition ?? {};
                                ol.layerDefinition.drawingInfo = { renderer: printRenderer };
                                ol.minScale = 0;
                                ol.maxScale = 0;
                                return ol;
                            }

                            const esriGeomType = geomTypeMap[cached.geometryType] ?? "esriGeometryPolygon";
                            const hasLabels = cached.labelingInfo.length > 0;
                            const printLabelingInfo = hasLabels
                                ? cached.labelingInfo.map((lc: any) => {
                                    if (!lc.symbol) return { ...lc, minScale: 0, maxScale: 0 };
                                    // Strip fields the print GP rejects: non-standard font decoration, lineWidth, lineHeight
                                    const { lineWidth: _lw, lineHeight: _lh, ...symRest } = lc.symbol;
                                    const sym = {
                                        ...symRest,
                                        ...(symRest.font ? { font: { ...symRest.font, decoration: "none" } } : {})
                                    };
                                    return { ...lc, minScale: 0, maxScale: 0, symbol: sym };
                                })
                                : [];

                            // Build a fields schema from the first feature's attributes so the print GP
                            // can resolve field references in the renderer (e.g. uniqueValue field1).
                            const sampleAttrs = cached.features[0]?.attributes ?? {};
                            const inferredFields = Object.entries(sampleAttrs).map(([name, val]) => {
                                let type = "esriFieldTypeString";
                                if (name === "OBJECTID") type = "esriFieldTypeOID";
                                else if (typeof val === "number") type = Number.isInteger(val) ? "esriFieldTypeInteger" : "esriFieldTypeDouble";
                                return { name, type, alias: name };
                            });
                            if (!inferredFields.find(f => f.name === "OBJECTID")) {
                                inferredFields.unshift({ name: "OBJECTID", type: "esriFieldTypeOID", alias: "OBJECTID" });
                            }

                            // For uniqueValue renderers, coerce each info's value to match the field type.
                            // The print GP matches strictly — integer fields need numeric values, not strings.
                            if (printRenderer?.type === "uniqueValue" && printRenderer.field1) {
                                const uvField = inferredFields.find(f => f.name === printRenderer.field1);
                                if (uvField && (uvField.type === "esriFieldTypeInteger" || uvField.type === "esriFieldTypeOID" || uvField.type === "esriFieldTypeDouble")) {
                                    printRenderer = {
                                        ...printRenderer,
                                        uniqueValueInfos: printRenderer.uniqueValueInfos?.map((info: any) => ({
                                            ...info,
                                            value: uvField.type === "esriFieldTypeDouble" ? parseFloat(info.value) : parseInt(info.value, 10)
                                        }))
                                    };
                                }
                            }

                            return {
                                id: ol.id,
                                title: ol.title,
                                opacity: ol.opacity ?? 1,
                                visibility: true,
                                showLabels: hasLabels,
                                minScale: 0,
                                maxScale: 0,
                                featureCollection: {
                                    layers: [{
                                        layerDefinition: {
                                            name: ol.title,
                                            geometryType: esriGeomType,
                                            minScale: 0,
                                            maxScale: 0,
                                            fields: inferredFields,
                                            ...(hasLabels ? { labelingInfo: printLabelingInfo } : {}),
                                            drawingInfo: {
                                                renderer: printRenderer,
                                                ...(hasLabels ? { showLabels: true } : {})
                                            }
                                        },
                                        featureSet: { geometryType: esriGeomType, features: cached.features }
                                    }]
                                }
                            };
                        };

                        webMapJson.operationalLayers = webMapJson.operationalLayers.map(processLayer);

                        // Reorder layers for correct rendering stack (bottom to top):
                        // 1. Basemap (VectorTileLayer)
                        // 2. Parcel featureCollection polygons (base cadastral layer)
                        // 3. Service feature layers (ArcGISFeatureLayer — overlay layers like CC, zoning, etc.
                        //    that fetch from service URL; these render on top of parcels)
                        // 4. Non-polygon featureCollections (points like Addresses — topmost)
                        {
                            const isParcelFc = (ol: any) => {
                                const c = printFeatureCacheRef.current[ol.id];
                                return !!c?.isParcelLayer;
                            };
                            const isPolygonFc = (ol: any) =>
                                !!ol.featureCollection &&
                                ol.featureCollection.layers?.[0]?.layerDefinition?.geometryType === "esriGeometryPolygon";

                            const basemapLayers = webMapJson.operationalLayers.filter(
                                (ol: any) => ol.type === "VectorTileLayer"
                            );
                            const parcelPolygonLayers = webMapJson.operationalLayers.filter(
                                (ol: any) => isPolygonFc(ol) && isParcelFc(ol)
                            );
                            const serviceFeatureLayers = webMapJson.operationalLayers.filter(
                                (ol: any) => !ol.featureCollection && ol.type !== "VectorTileLayer"
                            );
                            const fcOtherLayers = webMapJson.operationalLayers.filter(
                                (ol: any) => !!ol.featureCollection && !isParcelFc(ol)
                            );
                            webMapJson.operationalLayers = [
                                ...basemapLayers,
                                ...serviceFeatureLayers,
                                ...parcelPolygonLayers,
                                ...fcOtherLayers
                            ];
                        }

                        const labelLayers: any[] = [];

                        // Helper: compute centroid of the largest ring in a polygon feature
                        const polygonCentroid = (f: any): { x: number; y: number; spatialReference?: any } | null => {
                            if (!f.geometry?.rings?.length) return null;
                            let bestRing = f.geometry.rings[0];
                            let bestArea = 0;
                            for (const r of f.geometry.rings) {
                                let rxmin = Infinity, rxmax = -Infinity, rymin = Infinity, rymax = -Infinity;
                                for (const [x, y] of r) {
                                    if (x < rxmin) rxmin = x; if (x > rxmax) rxmax = x;
                                    if (y < rymin) rymin = y; if (y > rymax) rymax = y;
                                }
                                const area = (rxmax - rxmin) * (rymax - rymin);
                                if (area > bestArea) { bestArea = area; bestRing = r; }
                            }
                            let xmin = Infinity, xmax = -Infinity, ymin = Infinity, ymax = -Infinity;
                            for (const [x, y] of bestRing) {
                                if (x < xmin) xmin = x; if (x > xmax) xmax = x;
                                if (y < ymin) ymin = y; if (y > ymax) ymax = y;
                            }
                            return { x: (xmin + xmax) / 2, y: (ymin + ymax) / 2, spatialReference: f.geometry.spatialReference };
                        };

                        // Helper: build a text-symbol label layer entry
                        const makeLabelLayer = (id: string, title: string, textFeatures: any[]) => ({
                            id,
                            title,
                            opacity: 1,
                            showLabels: false,
                            minScale: 0,
                            maxScale: 0,
                            featureCollection: {
                                layers: [{
                                    layerDefinition: {
                                        name: title,
                                        geometryType: "esriGeometryPoint",
                                        fields: [
                                            { name: "OBJECTID", type: "esriFieldTypeOID", alias: "OBJECTID" },
                                            { name: "LABEL", type: "esriFieldTypeString", alias: "Label", length: 255 }
                                        ],
                                        drawingInfo: {
                                            renderer: {
                                                type: "simple",
                                                symbol: {
                                                    type: "esriTS", color: [0, 0, 0, 0],
                                                    backgroundColor: null, borderLineColor: null,
                                                    text: "", font: { family: "Arial", size: 8, style: "normal", weight: "normal", decoration: "none" }
                                                }
                                            }
                                        }
                                    },
                                    featureSet: { geometryType: "esriGeometryPoint", features: textFeatures }
                                }]
                            }
                        });

                        // Walk all layers recursively (including GroupLayer sublayers) to build label overlays.
                        const walkForLabels = (layers: any[]) => layers.forEach((ol: any) => {
                            if (ol.layerType === "GroupLayer" && Array.isArray(ol.layers)) { walkForLabels(ol.layers); return; }
                            if (!ol.featureCollection || !ol.showLabels) return;
                            const cached = printFeatureCacheRef.current[ol.id];
                            if (!cached || cached.labelingInfo.length === 0) return;

                            if (cached.isParcelLayer) {
                                // ── Parcel layer: existing PARCELWP / DISPLAY_ACRE centroid logic ──
                                const centroidFeatures = cached.features
                                    .filter((f: any) => f.geometry?.rings?.length > 0)
                                    .map((f: any) => {
                                        const pt = polygonCentroid(f)!;
                                        return {
                                            geometry: pt,
                                            attributes: { OBJECTID: f.attributes.OBJECTID, PARCELWP: f.attributes.PARCELWP, DISPLAY_ACRE: f.attributes.DISPLAY_ACRE }
                                        };
                                    });

                                if (centroidFeatures.length === 0) return;

                                const textFeatures = centroidFeatures.map((f: any) => {
                                    const rawParcel = (f.attributes.PARCELWP ?? "").split(".")[0];
                                    const parcelNum = parseInt(rawParcel, 10);
                                    const parcel = isNaN(parcelNum) ? "" : String(parcelNum);
                                    const acres = (f.attributes.DISPLAY_ACRE ?? "").trim();
                                    const labelText = parcel && acres ? `${parcel}\n${acres}` : parcel || acres;
                                    return {
                                        geometry: f.geometry,
                                        attributes: { OBJECTID: f.attributes.OBJECTID, LABEL: labelText },
                                        symbol: {
                                            type: "esriTS", color: [0, 0, 0, 255], backgroundColor: null, borderLineColor: null,
                                            haloColor: [255, 255, 255, 255], haloSize: 1,
                                            verticalAlignment: "middle", horizontalAlignment: "center",
                                            rightToLeft: false, angle: 0, xoffset: 0, yoffset: 0,
                                            text: labelText,
                                            font: { family: "Arial", size: 8, style: "normal", weight: "normal", decoration: "none" }
                                        }
                                    };
                                }).filter((f: any) => f.symbol.text.trim() !== "");

                                if (textFeatures.length > 0) {
                                    labelLayers.push(makeLabelLayer(ol.id + "_labels", ol.title + " Labels", textFeatures));
                                }
                            } else {
                                // ── Other labeled layers: use actual label expression, support points & polygons ──
                                cached.labelingInfo.forEach((lc: any, lcIdx: number) => {
                                    const expression: string = lc.labelExpression || lc.labelExpressionInfo?.expression || "";
                                    if (!expression) return;

                                    // Extract symbol color from the label class if available
                                    const symColor: number[] = lc.symbol?.color ?? [0, 0, 0, 255];
                                    const symFont = lc.symbol?.font ?? { family: "Arial", size: 9, style: "normal", weight: "normal", decoration: "none" };
                                    const symHalo: number[] = lc.symbol?.haloColor ?? [255, 255, 255, 255];
                                    const symHaloSize: number = lc.symbol?.haloSize ?? 1;

                                    const textFeatures = cached.features.map((f: any) => {
                                        // Build label text by substituting [FIELDNAME] tokens with actual attribute values
                                        const labelText = expression
                                            .replace(/\[([^\]]+)\]/g, (_: string, field: string) => {
                                                const val = f.attributes[field];
                                                return val != null ? String(val) : "";
                                            })
                                            .trim();

                                        if (!labelText) return null;

                                        // Resolve geometry: point layers use x/y directly; polygon layers compute centroid
                                        let pt: { x: number; y: number; spatialReference?: any } | null = null;
                                        if (f.geometry?.x != null && f.geometry?.y != null) {
                                            pt = { x: f.geometry.x, y: f.geometry.y, spatialReference: f.geometry.spatialReference };
                                        } else if (f.geometry?.rings?.length > 0) {
                                            pt = polygonCentroid(f);
                                        }
                                        if (!pt) return null;

                                        return {
                                            geometry: pt,
                                            attributes: { OBJECTID: f.attributes.OBJECTID, LABEL: labelText },
                                            symbol: {
                                                type: "esriTS",
                                                color: symColor,
                                                backgroundColor: null,
                                                borderLineColor: null,
                                                haloColor: symHalo,
                                                haloSize: symHaloSize,
                                                verticalAlignment: "middle",
                                                horizontalAlignment: "center",
                                                rightToLeft: false,
                                                angle: 0,
                                                xoffset: lc.symbol?.xoffset ?? 0,
                                                yoffset: lc.symbol?.yoffset ?? 0,
                                                text: labelText,
                                                font: symFont
                                            }
                                        };
                                    }).filter(Boolean);

                                    if (textFeatures.length > 0) {
                                        labelLayers.push(makeLabelLayer(
                                            `${ol.id}_labels_${lcIdx}`,
                                            `${ol.title} Labels`,
                                            textFeatures
                                        ));
                                    }
                                });
                            }
                        });
                        walkForLabels(webMapJson.operationalLayers);

                        // Insert label layers after their corresponding polygon layers
                        labelLayers.forEach(ll => {
                            const parentIdx = webMapJson.operationalLayers.findIndex((ol: any) => ol.id === ll.id.replace("_labels", ""));
                            if (parentIdx >= 0) {
                                webMapJson.operationalLayers.splice(parentIdx + 1, 0, ll);
                            } else {
                                webMapJson.operationalLayers.push(ll);
                            }
                        });
                        // Extract WMS layers from operationalLayers
                        const wmsEntries = webMapJson.operationalLayers.filter(
                            (ol: any) => ol.type === "wms"
                        );
                        if (wmsEntries.length === 0) {
                            const finalJson = JSON.stringify(webMapJson);
                            console.log("[Print] Web_Map_as_JSON =", finalJson);
                            query.Web_Map_as_JSON = finalJson;
                            return;
                        }

                        // Remove ALL WMS entries from operationalLayers
                        webMapJson.operationalLayers = webMapJson.operationalLayers.filter(
                            (ol: any) => ol.type !== "wms"
                        );

                        // Use the first WMS entry (deduplicate) and build a baseMap
                        const wms = wmsEntries[0];
                        // Find the live WMS basemap layer (added by activeBasemap watcher) to get real URL
                        const liveWmsLayer = webmap.layers.find((l: any) => l._isBasemapLayer && l._originalWmsUrl) as any;
                        const url = liveWmsLayer?._originalWmsUrl || wms.url;

                        webMapJson.baseMap = {
                            title: wms.title || "WMS Basemap",
                            baseMapLayers: [{
                                id: wms.id,
                                title: wms.title,
                                url: url,
                                type: "wms",
                                visibility: true,
                                opacity: wms.opacity ?? 1,
                                visibleLayers: wms.visibleLayers,
                                version: wms.version || "1.3.0"
                            }]
                        };

                        const finalJson = JSON.stringify(webMapJson);
                        console.log("[Print] Web_Map_as_JSON =", finalJson);
                        query.Web_Map_as_JSON = finalJson;
                    } catch (e) {
                        console.error("[Print] Failed to modify print request:", e);
                    }
                },
                after: (response: any) => {
                    try {
                        const data = response?.data;
                        if (data) console.log("[Print] GP response =", JSON.stringify(data));
                    } catch (_) {}
                },
                error: (e: any) => {
                    console.error("[Print] GP error =", e?.details?.httpStatus, e?.message, JSON.stringify(e?.details));
                }
            });

            const scaleBar = new ScaleBar({ view, unit: "dual", style: "ruler" });
            view.ui.add(scaleBar, { position: "bottom-left", index: 0 });

            view.ui.components = ["attribution"];

            const topLeftRow = document.createElement("div");
            topLeftRow.className = "custom-top-left-row";
            Object.assign(topLeftRow.style, {
                display: "flex",
                gap: "8px",
            });
            view.ui.add(topLeftRow, { position: "top-left", index: 0 });

            const zoomNode = document.createElement("div");

            topLeftRow.appendChild(zoomNode);

            const zoom = new Zoom({ view, container: zoomNode });

            const home = new Home({ view });
            view.ui.add(home, { position: "top-left", index: 1 });

            const gsv = googleStreetView(view, {
                iconUrl: streetViewIcon,
                startAtCenter: true,
                resizable: true
            });

            const draw = drawWidget(view);

            // Detect mobile for widget placement
            const isMobile = window.innerWidth <= 768;
            const widgetPlacement = isMobile ? "right" : "bottom";

            const drawExpand = new Expand({
                view,
                content: draw.element as any,
                group: "top-right-tools",
                expandTooltip: "Draw",
                expandIcon: "annotate-tool",
                collapseIcon: "annotate-tool",
                placement: "bottom",
                container: wrap(),
                mode: "floating"
            });

            // keep visibility in sync
            draw.setVisible(drawExpand.expanded);
            drawExpand.watch("expanded", (v: boolean) => draw.setVisible(!!v));

            const gsvExpand = new Expand({
                view,
                content: gsv.element as any,
                expandTooltip: "Street View",
                expanded: false,
                expandIcon: "person-view",
                collapseIcon: "person-view",
                mode: "floating"
            });
            view.ui.add(gsvExpand, { position: "top-left", index: 2 });
            gsv.setVisible(gsvExpand.expanded);
            gsvExpand.watch("expanded", (v) => gsv.setVisible(!!v));

            const measurementDiv = document.createElement("div");
            measurementDiv.style.minWidth = "fit-content";
            measurementDiv.style.minHeight = "fit-content";

            const measurement = new Measurement({
                view,
                container: measurementDiv,
                linearUnit: "feet",
                areaUnit: "acres",
            });

            const mBar = document.createElement("div");
            mBar.style.cssText = "display: flex; gap: 8px; margin: 8px; align-items: center;";

            const btnDist = document.createElement("button");
            btnDist.className = "esri-button";
            btnDist.title = "Distance";
            btnDist.setAttribute("aria-label", "Distance");
            btnDist.innerHTML = `<span class="esri-icon esri-icon-measure-line" aria-hidden="true"></span>`;

            const btnArea = document.createElement("button");
            btnArea.className = "esri-button";
            btnArea.title = "Area";
            btnArea.setAttribute("aria-label", "Area");
            btnArea.innerHTML = `<span class="esri-icon esri-icon-measure-area" aria-hidden="true"></span>`;

            const btnClear = document.createElement("button");
            btnClear.className = "esri-button";
            btnClear.title = "Clear";
            btnClear.setAttribute("aria-label", "Clear");
            btnClear.innerHTML = `<span class="esri-icon esri-icon-trash" aria-hidden="true"></span>`;

            mBar.append(btnDist, btnArea, btnClear);
            styleEl.textContent += `
                .esri-button { display:inline-grid; place-items:center; width:32px; height:32px; padding:0; }
                .esri-button .esri-icon { font-size:16px; line-height:1; }
                .custom-top-right-row .esri-button,
                .custom-top-right-row .esri-button:hover,
                .custom-top-right-row .esri-button:focus,
                .custom-top-right-row .esri-button:active {
                  color: #2b2b2b !important;
                  background: #fff !important;
                  border: 1px solid #dcdcdc !important;
                }

                .custom-top-right-row .esri-button:hover { background: #f5f5f5 !important; }
                .custom-top-right-row .esri-button[aria-pressed="true"] {
                  background: #e6f2fb !important;
                  color: #005e95 !important;
                  border-color: #9bc8e5 !important;
                }

                .custom-top-right-row .esri-button .esri-icon { font-size: 16px; line-height: 1; }
            `;

            const measureWrap = document.createElement("div");
            measureWrap.append(mBar, measurementDiv);

            const measureExpand = new Expand({
                view,
                content: measureWrap,
                group: "top-right-tools",
                expandTooltip: "Measure",
                expandIcon: "measure",
                collapseIcon: "measure",
                placement: widgetPlacement,
                container: wrap(),
                mode: "floating"
            });
            const legendExpand = new Expand({ view, content: legend, group: "top-right-tools", expandTooltip: "Legend", collapseIcon: "legend", placement: widgetPlacement, container: wrap(), mode: "floating" });
            const llExpand = new Expand({ view, content: layerList, group: "top-right-tools", expandTooltip: "Layers", collapseIcon: "layers", placement: widgetPlacement, container: wrap(), mode: "floating" });
            const bgExpand = new Expand({ view, content: basemapGallery, group: "top-right-tools", expandTooltip: "Basemaps", collapseIcon: "basemap", placement: widgetPlacement, container: wrap(), mode: "floating" });

            function setTool(tool: "distance" | "area" | null) {
                measurement.clear();
                (measurement as any).activeTool = tool;
            }

            const m = measurement as any;

            function startOrToggle(tool: "distance" | "area") {
                if (m.activeTool === tool) {
                    m.clear();
                    m.activeTool = null;
                    return;
                }

                m.clear();
                m.activeTool = null;
                requestAnimationFrame(() => {
                    m.activeTool = tool;
                });
            }

            btnDist.onclick = () => startOrToggle("distance");
            btnArea.onclick = () => startOrToggle("area");
            btnClear.onclick = () => {
                m.clear();
                m.activeTool = null;
            };

            m.watch("activeTool", (tool: "distance" | "area" | null) => {
                btnDist.setAttribute("aria-pressed", String(tool === "distance"));
                btnArea.setAttribute("aria-pressed", String(tool === "area"));
                view.popupEnabled = !tool;
                (view as any).__measuring = !!tool;
            });

            (measurement as any).watch("activeTool", (tool: "distance" | "area" | null) => {
                view.popupEnabled = !tool;
                (view as any).__measuring = !!tool;
            });

            measureExpand.watch("expanded", (open: boolean) => {
                if (open) {
                    requestAnimationFrame(() => m.renderNow?.());
                } else {
                    m.clear();
                    m.activeTool = null;
                    view.popupEnabled = true;
                    btnDist.setAttribute("aria-pressed", "false");
                    btnArea.setAttribute("aria-pressed", "false");
                }
            });

            const printExpand = new Expand({ view, content: printContainer, group: "top-right-tools", expandTooltip: "Print", expandIcon: "print", collapseIcon: "print", placement: widgetPlacement, container: wrap(), mode: "floating" });

            // Fix calcite-action button flex property to center icons
            function fixCalciteActionButtons() {
                const calciteActions = document.querySelectorAll('calcite-action');
                calciteActions.forEach((action) => {
                    const shadowRoot = action.shadowRoot;
                    if (shadowRoot) {
                        const button = shadowRoot.querySelector('.button');
                        if (button && !(button as any).__flexFixed) {
                            (button as HTMLElement).style.flex = 'none';
                            (button as HTMLElement).style.justifyContent = 'center';
                            (button as any).__flexFixed = true;
                        }
                    }
                });
            }

            // Run immediately and after delays to catch dynamically added elements
            setTimeout(fixCalciteActionButtons, 100);
            setTimeout(fixCalciteActionButtons, 500);
            setTimeout(fixCalciteActionButtons, 1000);

            // Watch for new calcite-action elements being added
            const observer = new MutationObserver(() => {
                fixCalciteActionButtons();
            });
            observer.observe(document.body, { childList: true, subtree: true });

            const coord = installCompactCoordinate(view, {
                decimals: 6,
                pinImageUrl: require("./custom-widgets/images/esriGreenPin16x26.png") as string,
                attachToUi: false
            });

            view.ui.add(coord.element as any, "manual");

            const pillEl = coord.element as HTMLElement;
            Object.assign(pillEl.style, { position: "absolute", left: "8px", bottom: "70px", zIndex: "5" });

            const vc = view.container as HTMLElement | null;
            const sbEl = vc ? (vc.querySelector(".esri-scale-bar") as HTMLElement | null) : null;

            function placeAboveScale() {
                if (!vc || !sbEl) return;
                const cr = vc.getBoundingClientRect();
                const sr = sbEl.getBoundingClientRect();
                const bottomDistance = Math.max(0, cr.bottom - sr.top);
                const gap = 12;
                pillEl.style.bottom = `${bottomDistance + gap}px`;
            }

            placeAboveScale();
            const ro1 = new ResizeObserver(placeAboveScale);
            const ro2 = new ResizeObserver(placeAboveScale);
            if (sbEl) ro1.observe(sbEl);
            if (vc) ro2.observe(vc);
            window.addEventListener("resize", placeAboveScale);
            const uiHandle = view.watch("size", placeAboveScale);

            (view as any).__widgetCleanup = () => {
                try { view.ui.remove(gsvExpand); } catch { }
                gsvExpand.destroy();
                gsv.destroy();

                printExpand.destroy(); legendExpand.destroy(); llExpand.destroy(); bgExpand.destroy();
                measureExpand.destroy(); measurement.destroy();
                printEl.destroy(); legend.destroy(); layerList.destroy(); basemapGallery.destroy();

                try { ro1.disconnect(); ro2.disconnect(); } catch { }
                uiHandle.remove();
                coord.destroy();

                scaleBar.destroy();
                styleEl.remove();
            };
        }, (err: any) => {
            if (err?.name === "AbortError" || err?.message === "Aborted") return;
            console.error("[ArcGISMap] view.when error", err);
        });

        return () => {
            const v = viewRef.current as any;
            if (v && typeof v.__widgetCleanup === "function") v.__widgetCleanup();
            view.destroy();
        };
    }, [itemId]);

    useImperativeHandle(ref, () => ({
        zoomTo({ latitude, longitude }) {
            viewRef.current?.goTo({ center: [longitude, latitude], zoom: 18 }).catch(() => { });
        },
        async highlightAndZoom(geomJson?: any, center?: { latitude: number; longitude: number }, gisLink?: string) {
            const view = viewRef.current;
            const hl = highlightLayerRef.current;
            if (!view || !hl) return;
            hl.removeAll();

            const highlightParcel = (parcelGeometry: __esri.Geometry) => {
                const symbol = new SimpleFillSymbol({
                    color: new Color([0, 121, 193, 0.2]),
                    outline: new SimpleLineSymbol({
                        color: new Color([0, 121, 193, 1]),
                        width: 2
                    }),
                });
                hl.add(new Graphic({ geometry: parcelGeometry, symbol }));
                view.goTo({ target: parcelGeometry, zoom: 18 }).catch(() => { });
            };

            // If we have a GISLINK, query the geometry layer directly for the parcel polygon
            if (gisLink) {
                try {
                    if (!geomLayerRef.current) {
                        const geometryLayerUrl = config.geometryLayer || config.parcelLayer.replace(/\/\d+$/, '/46');
                        geomLayerRef.current = new FeatureLayer({ url: geometryLayerUrl });
                        await geomLayerRef.current.load();
                    }
                    const geomLayer = geomLayerRef.current;

                    const query = geomLayer.createQuery();
                    query.where = `GISLINK = '${gisLink}'`;
                    query.returnGeometry = true;
                    query.outFields = ["GISLINK"];
                    query.num = 1;

                    const results = await geomLayer.queryFeatures(query);
                    const parcelGeom = results.features[0]?.geometry;
                    if (parcelGeom) {
                        highlightParcel(parcelGeom);
                        return;
                    }
                } catch (error) {
                    console.error("Error querying parcel by GISLINK:", error);
                }
            }

            if (geomJson) {
                const geometry = geometryJsonUtils.fromJSON(geomJson);

                // If it's a point or polyline (not a parcel polygon), query for the parcel at that location
                if (geometry?.type === "point" || geometry?.type === "polyline") {
                    try {
                        const webmap = view.map as WebMap;
                        await webmap.loadAll();

                        const parcelLayer = webmap.allLayers.find((layer: any) => {
                            return layer.title?.toLowerCase().includes("parcel");
                        }) as __esri.FeatureLayer;

                        if (parcelLayer) {
                            const queryGeometry = geometry.type === "polyline"
                                ? geometry.extent!.center
                                : geometry;

                            const query = parcelLayer.createQuery();
                            query.geometry = queryGeometry as __esri.Geometry;
                            query.spatialRelationship = "intersects";
                            query.returnGeometry = true;
                            query.outFields = ["*"];

                            const results = await parcelLayer.queryFeatures(query);
                            const parcelGeom = results.features[0]?.geometry;
                            if (parcelGeom) {
                                highlightParcel(parcelGeom);
                                return;
                            }
                        }
                    } catch (error) {
                        console.error("Error querying parcel:", error);
                    }

                    // Fallback: just zoom to the geometry
                    view.goTo({ target: geometry, zoom: 18 }).catch(() => { });
                    return;
                }

                // If it's already a polygon, just display it
                if (geometry) {
                    highlightParcel(geometry);
                }
                return;
            }
            if (center) {
                view.goTo({ center: [center.longitude, center.latitude], zoom: 18 }).catch(() => { });
            }
        },
        async queryParcelAtPoint(point: __esri.Point, view: MapView, hl: GraphicsLayer) {
            try {
                const webmap = view.map as WebMap;
                await webmap.loadAll();

                const parcelLayer = webmap.allLayers.find((layer: any) => {
                    const url = layer.url || "";
                    return layer.title?.toLowerCase().includes("parcel");
                }) as __esri.FeatureLayer;

                if (!parcelLayer) {
                    console.warn("Parcel layer not found");
                    view.goTo({ target: point, zoom: 18 }).catch(() => { });
                    return;
                }

                const query = parcelLayer.createQuery();
                query.geometry = point;
                query.spatialRelationship = "intersects";
                query.returnGeometry = true;
                query.outFields = ["*"];

                const results = await parcelLayer.queryFeatures(query);

                if (results.features.length > 0) {
                    const parcelGeometry = results.features[0].geometry;
                    const symbol = new SimpleFillSymbol({
                        color: new Color([0, 121, 193, 0.2]),
                        outline: new SimpleLineSymbol({
                            color: new Color([0, 121, 193, 1]),
                            width: 2
                        }),
                    });
                    const g = new Graphic({ geometry: parcelGeometry, symbol });
                    hl.add(g);
                    view.goTo({ target: parcelGeometry, zoom: 18 }).catch(() => { });
                } else {
                    view.goTo({ target: point, zoom: 18 }).catch(() => { });
                }
            } catch (error) {
                console.error("Error querying parcel:", error);
                view.goTo({ target: point, zoom: 18 }).catch(() => { });
            }
        }
    }));

    useEffect(() => {
        if (!viewRef.current) return;
        const view = viewRef.current;

        const isParcelOrBuilding = (layer: any): { ok: boolean; id?: number; title?: string } => {
            try {
                const title = (layer?.title ?? "").toString().toLowerCase();
                const url: string = String(layer?.url ?? "");
                const idPart = url.split("/").filter(Boolean).pop();
                const parsedId = Number(idPart);

                // Check if this layer URL matches configured parcel or geometry layers
                const geometryLayerUrl = config.geometryLayer || config.parcelLayer.replace(/\/\d+$/, '/46');
                const parcelLayerUrl = config.parcelLayer;
                const isConfiguredParcelLayer = url === geometryLayerUrl || url === parcelLayerUrl ||
                    url.startsWith(geometryLayerUrl.replace(/\/\d+$/, '')) ||
                    url.startsWith(parcelLayerUrl.replace(/\/\d+$/, ''));

                const isParcel = isConfiguredParcelLayer || title.includes("parcel") || title.includes("cadastral");
                const isBuilding = title.includes("building");
                return { ok: isParcel || isBuilding, id: Number.isFinite(parsedId) ? parsedId : undefined, title: layer?.title };
            } catch {
                return { ok: false };
            }
        };

        const onClick = async (evt: __esri.ViewClickEvent) => {
            const v: any = view;
            if (!view || v.__measuring || v.__drawing) {
                return;
            }

            const mp = evt.mapPoint;
            if (!mp) {
                return;
            }

            // Only allow parcel identification when zoomed in enough
            if (view.scale > config.parcelScale) {
                return; // Too zoomed out, ignore click
            }

            const center = toLatLong(mp);

            // Show loading spinner
            onLoadingStart?.();

            // Always query the parcel feature service directly from config
            let g: __esri.Graphic | undefined;
            let layer: any;
            let geomJson: any;

            try {
                // Step 1: Query Layer 46 to get the parcel at the click point (spatial query)
                const geometryLayerUrl = config.geometryLayer || config.parcelLayer.replace(/\/\d+$/, '/46');
                const parcelGeometryLayer = new FeatureLayer({ url: geometryLayerUrl });
                await parcelGeometryLayer.load();

                const geometryQuery = parcelGeometryLayer.createQuery();
                geometryQuery.geometry = mp;
                geometryQuery.spatialRelationship = "intersects";
                geometryQuery.returnGeometry = true;
                geometryQuery.outFields = ["GISLINK"];
                geometryQuery.where = "GISLINK IS NOT NULL AND GISLINK NOT IN (' ', '')";
                geometryQuery.num = 1;
                geometryQuery.outSpatialReference = mp.spatialReference;

                const geometryResults = await parcelGeometryLayer.queryFeatures(geometryQuery);

                if (geometryResults.features.length > 0) {
                    const gisLink = geometryResults.features[0].attributes?.GISLINK;
                    let geometry = geometryResults.features[0].geometry;

                    const polyJson = geometry?.toJSON?.();
                    if (polyJson?.rings?.length > 1) {
                        const cx = mp.x, cy = mp.y;
                        const ringDists = polyJson.rings.map((ring: number[][]) => {
                            const xs = ring.map((p) => p[0]);
                            const ys = ring.map((p) => p[1]);
                            const rx = (Math.min(...xs) + Math.max(...xs)) / 2;
                            const ry = (Math.min(...ys) + Math.max(...ys)) / 2;
                            return { ring, dist: Math.hypot(cx - rx, cy - ry) };
                        });
                        const minDist = Math.min(...ringDists.map((r: any) => r.dist));
                        const threshold = Math.max(minDist * 5, 500); // keep rings within 5x closest, min 500 units
                        const nearRings = ringDists.filter((r: any) => r.dist <= threshold).map((r: any) => r.ring);
                        geometry = geometryJsonUtils.fromJSON({ ...polyJson, rings: nearRings }) as __esri.Polygon;
                    }

                    if (gisLink) {
                        // Query Layer 47 by GISLINK for full attributes
                        const attrTableLayer = new FeatureLayer({ url: config.parcelLayer });
                        await attrTableLayer.load();

                        const attributeQuery = attrTableLayer.createQuery();
                        attributeQuery.where = `GISLINK = '${gisLink}'`;
                        attributeQuery.outFields = ["*"];
                        attributeQuery.returnGeometry = false;

                        const attributeResults = await attrTableLayer.queryFeatures(attributeQuery);

                        if (attributeResults.features.length > 0) {
                            g = new Graphic({
                                geometry: geometry,
                                attributes: attributeResults.features[0].attributes
                            });
                            layer = attrTableLayer;
                            geomJson = g.geometry?.toJSON?.();
                        } else {
                            g = geometryResults.features[0];
                            layer = parcelGeometryLayer;
                            geomJson = g.geometry?.toJSON?.();
                        }
                    }
                }
            } catch (error) {
                console.error('Error querying parcel layer:', error);
                onLoadingEnd?.();
                return;
            }

            if (!g) {
                onLoadingEnd?.();
                return;
            }

            const layerInfo = isParcelOrBuilding(layer);

            let attrs = g.attributes ?? {};

            onMapIdentify?.({
                center,
                geometryJson: geomJson,
                attributes: attrs,
                layerId: layerInfo.id,
                layerTitle: layerInfo.title,
            });

            try {
                (ref as any)?.current?.highlightAndZoom?.(geomJson, center);
            } catch { }

            // Hide loading spinner
            onLoadingEnd?.();
        };

        // Update cursor based on scale
        const updateCursor = () => {
            if (!view.container) return;
            if (view.scale <= config.parcelScale) {
                view.container.style.cursor = "pointer";
            } else {
                view.container.style.cursor = "default";
            }
        };

        // Set initial cursor
        updateCursor();

        // Update cursor on zoom
        const scaleWatchHandle = view.watch("scale", updateCursor);

        const handle = view.on("click", onClick);
        return () => {
            handle.remove();
            scaleWatchHandle.remove();
        };
    }, [itemId, onMapIdentify, onCondoUnitsFound, onLoadingStart, onLoadingEnd]);

    return <div style={MapDivStyle} ref={mapDiv} />;
});

export default ArcGISMap;

function toLatLong(p: __esri.Point): { latitude: number; longitude: number } {
    const g = (p.spatialReference?.isWGS84 || p.spatialReference?.isGeographic)
        ? p
        : (webMercatorUtils.webMercatorToGeographic(p) as Point);

    return { latitude: g.y as number, longitude: g.x as number };
}
